import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { CalendarCheck, CalendarRange, MapPin, X, Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const blankEvent = () => ({
    event_name: "", event_description: "", host: "",
    location_id: "", ranges: [{ start_datetime: "", end_datetime: "" }],
    check_in_required: true,
  });

// ─── Step 1: Select Event Type ───────────────────────────────────────────────
const SelectEventType = ({ selectedType, setSelectedType }) => (
  <>
    <div className="text-center mb-16">
      <h1 className="text-3xl font-semibold text-gray-900 mb-3">
        Select your event type
      </h1>
      <p className="text-gray-500">Please select to proceed</p>
    </div>

    <div className="flex gap-6 mb-20">
      {/* Single Event */}
      <div
        className={`w-[320px] h-40 border-2 rounded-2xl p-6 cursor-pointer transition-all ${
          selectedType === "Single"
            ? "border-blue-700 bg-white"
            : "border-gray-200 bg-white hover:border-gray-300"
        }`}
        onClick={() => setSelectedType("Single")}
      >
        <div className="flex flex-col items-center justify-center h-full gap-3">
          <CalendarCheck className="w-12 h-12 text-gray-800" strokeWidth={1.5} />
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900">Single Event</h3>
            <p className="text-xs text-gray-500 mt-1">
              Create an event with one or more time ranges
            </p>
          </div>
        </div>
      </div>

      {/* Bulk Create */}
      <div
        className={`w-[320px] h-40 border-2 rounded-2xl p-6 cursor-pointer transition-all ${
          selectedType === "Bulk"
            ? "border-blue-700 bg-white"
            : "border-gray-200 bg-white hover:border-gray-300"
        }`}
        onClick={() => setSelectedType("Bulk")}
      >
        <div className="flex flex-col items-center justify-center h-full gap-3">
          <CalendarRange className="w-12 h-12 text-gray-800" strokeWidth={1.5} />
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900">Bulk Create</h3>
            <p className="text-xs text-gray-500 mt-1">
              Create multiple events in a single request
            </p>
          </div>
        </div>
      </div>
    </div>
  </>
);

// ─── Step 2: Event Details Form ───────────────────────────────────────────────
const EventForm = ({
  formData,
  setFormData,
  hosts,
  locations,
  errors,
  isBulk,
}) => {
  // Add a new date/time range
  const addRange = () => {
    setFormData({
      ...formData,
      ranges: [...formData.ranges, { start_datetime: "", end_datetime: "" }],
    });
  };

  // Remove a range by index
  const removeRange = (index) => {
    const updated = formData.ranges.filter((_, i) => i !== index);
    setFormData({ ...formData, ranges: updated });
  };

  // Update a specific range field
  const updateRange = (index, field, value) => {
    const updated = formData.ranges.map((r, i) =>
      i === index ? { ...r, [field]: value } : r
    );
    setFormData({ ...formData, ranges: updated });
  };

  // Add selected location as tag (prevent duplicates)
  const handleLocationSelect = (e) => {
    const val = e.target.value;
    if (!val || formData.location_ids.includes(val)) {
      e.target.value = "";
      return;
    }
    setFormData({ ...formData, location_ids: [...formData.location_ids, val] });
    e.target.value = "";
  };

  // Remove a location tag
  const removeLocation = (val) => {
    setFormData({
      ...formData,
      location_ids: formData.location_ids.filter((id) => id !== val),
    });
  };

  return (
    <div className="max-w-6xl mx-auto w-full pl-12">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          {isBulk ? "Bulk Create Events" : "Create Event"}
        </h1>
        <p className="text-gray-500">Please fill the event details to proceed</p>
      </div>

      {/* ── Row 1: Event Name + Description ── */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Event Name
            {errors.event_name && (
              <span className="text-red-500 text-sm ml-2">{errors.event_name}</span>
            )}
          </label>
          <input
            type="text"
            placeholder="e.g. ACME Innovation Day"
            value={formData.event_name}
            maxLength={200}
            onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Description
            <span className="text-gray-400 text-xs font-normal ml-2">(Optional)</span>
          </label>
          <input
            type="text"
            placeholder="Brief description of the event"
            value={formData.event_description}
            maxLength={2000}
            onChange={(e) =>
              setFormData({ ...formData, event_description: e.target.value })
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* ── Row 2: Host + Location ── */}
      <div className="grid grid-cols-2 gap-4 mb-2">
        {/* Host dropdown */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Host
            {errors.host && (
              <span className="text-red-500 text-sm ml-2">{errors.host}</span>
            )}
          </label>
          <select
            value={formData.host}
            onChange={(e) => setFormData({ ...formData, host: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a host</option>
            {hosts.map((h, i) => (
              <option key={i} value={h.name}>
                {h.name}
              </option>
            ))}
          </select>
        </div>

        {/* Location dropdown + selected tags */}
        <div>
  <label className="block text-gray-700 mb-2 font-medium">
    Location
    {errors.location_id && (
      <span className="text-red-500 text-sm ml-2">{errors.location_id}</span>
    )}
  </label>
  <select
    value={formData.location_id}
    onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <option value="">Select a location</option>
    {locations.map((loc) => (
      <option key={loc._id} value={loc._id}>{loc.location_name}</option>
    ))}
  </select>
</div>
      </div>

      {/* ── Date & Time Ranges ── */}
      <div className="mb-2 mt-6">
        <label className="block text-gray-700 mb-1 font-medium">
          Date & Time Ranges
          {errors.ranges && (
            <span className="text-red-500 text-sm ml-2">{errors.ranges}</span>
          )}
        </label>
        <p className="text-gray-400 text-xs mb-3">
          Add one or more date/time ranges. Each end time must be after its start time.
        </p>

        <div className="space-y-3">
          {formData.ranges.map((range, index) => (
            <div key={index} className="grid grid-cols-2 gap-4 items-end">
              <div>
                <label className="block text-gray-600 mb-1.5 text-sm font-medium">
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={range.start_datetime}
                  onChange={(e) =>
                    updateRange(index, "start_datetime", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-gray-600 mb-1.5 text-sm font-medium">
                    End Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={range.end_datetime}
                    onChange={(e) =>
                      updateRange(index, "end_datetime", e.target.value)
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {formData.ranges.length > 1 && (
                  <button
                    onClick={() => removeRange(index)}
                    className="p-3 border border-gray-300 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-300 transition-colors bg-white"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={addRange}
          className="mt-3 flex items-center gap-2 text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg px-4 py-2 hover:border-blue-500 hover:text-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Another Time Range
        </button>
      </div>


<div className="flex items-center justify-between mt-6">
  <div>
    <p className="text-gray-700 font-medium">Check-in required</p>
    <p className="text-gray-400 text-sm mt-0.5">
      Visitors must check in to attend this event
    </p>
  </div>
  <button
    onClick={() =>
      setFormData({ ...formData, check_in_required: !formData.check_in_required })
    }
    className={`relative w-[52px] h-[24px] rounded-full transition-colors duration-200 flex-shrink-0 ${
      formData.check_in_required ? "bg-blue-700" : "bg-gray-300"
    }`}
  >
    <span
      className={`absolute top-[3px] w-[18px] h-[18px] bg-white rounded-full shadow transition-all duration-200 ${
        formData.check_in_required ? "left-[31px]" : "left-[3px]"
      }`}
    />
  </button>
</div>
    </div>
  );
};

const BulkEventForm = ({ events, setEvents, hosts, locations, errors }) => {
    const [collapsed, setCollapsed] = useState({});
  
    const updateEvent = (index, field, value) =>
      setEvents(events.map((ev, i) => (i === index ? { ...ev, [field]: value } : ev)));
  
    const addEvent = () => {
      if (events.length >= 100) return;
      setEvents([...events, blankEvent()]);
    };
  
    const removeEvent = (index) =>
      setEvents(events.filter((_, i) => i !== index));
  
    const toggleCollapse = (index) =>
      setCollapsed((prev) => ({ ...prev, [index]: !prev[index] }));
  
    const handleLocationSelect = (index, e) => {
      const val = e.target.value;
      if (!val || events[index].location_ids.includes(val)) { e.target.value = ""; return; }
      updateEvent(index, "location_ids", [...events[index].location_ids, val]);
      e.target.value = "";
    };
  
    const removeLocation = (index, id) =>
      updateEvent(index, "location_ids", events[index].location_ids.filter((l) => l !== id));
  
    const updateRange = (evIndex, rIndex, field, value) => {
      const updated = events[evIndex].ranges.map((r, i) =>
        i === rIndex ? { ...r, [field]: value } : r
      );
      updateEvent(evIndex, "ranges", updated);
    };
  
    const addRange = (index) =>
      updateEvent(index, "ranges", [...events[index].ranges, { start_datetime: "", end_datetime: "" }]);
  
    const removeRange = (evIndex, rIndex) =>
      updateEvent(evIndex, "ranges", events[evIndex].ranges.filter((_, i) => i !== rIndex));
  
    return (
      <div className="max-w-6xl mx-auto w-full pl-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Bulk Create Events</h1>
          <p className="text-gray-500">Fill details for each event</p>
        </div>
  
        <div className="space-y-4">
          {events.map((ev, index) => {
            const isCollapsed = collapsed[index];
            const eventErrors = errors[index] || {};
            return (
              <div key={index} className="border border-gray-200 rounded-xl bg-white overflow-hidden">
                {/* Header */}
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleCollapse(index)}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-semibold flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="font-medium text-gray-800 text-sm">
                      {ev.event_name || <span className="text-gray-400 font-normal">Untitled Event</span>}
                    </span>
                    {Object.keys(eventErrors).length > 0 && (
                      <span className="text-xs text-red-500 font-medium">— has errors</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {events.length > 1 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); removeEvent(index); }}
                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                   {isCollapsed
  ? <ChevronDown className="w-4 h-4 text-gray-400" />
  : <ChevronUp className="w-4 h-4 text-gray-400" />
}
                  </div>
                </div>
  
                {/* Body */}
                {!isCollapsed && (
                  <div className="px-5 pb-5 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-4 mt-4 mb-4">
                      <div>
                        <label className="block text-gray-700 mb-2 font-medium text-sm">
                          Event Name
                          {eventErrors.event_name && <span className="text-red-500 text-xs ml-2">{eventErrors.event_name}</span>}
                        </label>
                        <input type="text" placeholder="e.g. ACME Innovation Day" value={ev.event_name} maxLength={200}
                          onChange={(e) => updateEvent(index, "event_name", e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-2 font-medium text-sm">
                          Description <span className="text-gray-400 text-xs font-normal ml-1">(Optional)</span>
                        </label>
                        <input type="text" placeholder="Brief description" value={ev.event_description} maxLength={2000}
                          onChange={(e) => updateEvent(index, "event_description", e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                    </div>
  
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-gray-700 mb-2 font-medium text-sm">
                          Host {eventErrors.host && <span className="text-red-500 text-xs ml-2">{eventErrors.host}</span>}
                        </label>
                        <select value={ev.host} onChange={(e) => updateEvent(index, "host", e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                          <option value="">Select a host</option>
                          {hosts.map((h, i) => <option key={i} value={h.name}>{h.name}</option>)}
                        </select>
                      </div>
                      <div>
  <label className="block text-gray-700 mb-2 font-medium text-sm">
    Location {eventErrors.location_id && <span className="text-red-500 text-xs ml-2">{eventErrors.location_id}</span>}
  </label>
  <select
    value={ev.location_id}
    onChange={(e) => updateEvent(index, "location_id", e.target.value)}
    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
  >
    <option value="">Select a location</option>
    {locations.map((loc) => (
      <option key={loc._id} value={loc._id}>{loc.location_name}</option>
    ))}
  </select>
</div>
                    </div>
  
                    <div className="mb-2">
                      <label className="block text-gray-700 mb-1 font-medium text-sm">
                        Date & Time Ranges
                        {eventErrors.ranges && <span className="text-red-500 text-xs ml-2">{eventErrors.ranges}</span>}
                      </label>
                      <p className="text-gray-400 text-xs mb-3">Each end time must be after its start time.</p>
                      <div className="space-y-3">
                        {ev.ranges.map((range, rIndex) => (
                          <div key={rIndex} className="grid grid-cols-2 gap-4 items-end">
                            <div>
                              {rIndex === 0 && <label className="block text-gray-600 mb-1.5 text-sm font-medium">Start Date & Time</label>}
                              <input type="datetime-local" value={range.start_datetime}
                                onChange={(e) => updateRange(index, rIndex, "start_datetime", e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                            </div>
                            <div className="flex gap-3 items-end">
                              <div className="flex-1">
                                {rIndex === 0 && <label className="block text-gray-600 mb-1.5 text-sm font-medium">End Date & Time</label>}
                                <input type="datetime-local" value={range.end_datetime}
                                  onChange={(e) => updateRange(index, rIndex, "end_datetime", e.target.value)}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                              </div>
                              {ev.ranges.length > 1 && (
                                <button onClick={() => removeRange(index, rIndex)}
                                  className="p-3 border border-gray-300 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-300 transition-colors bg-white">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => addRange(index)}
                        className="mt-3 flex items-center gap-2 text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg px-4 py-2 hover:border-blue-500 hover:text-blue-700 transition-colors">
                        <Plus className="w-4 h-4" /> Add Another Time Range
                      </button>
                    </div>
  
                    <div className="flex items-center justify-between mt-4">
                      <div>
                        <p className="text-gray-700 font-medium text-sm">Check-in required</p>
                        <p className="text-gray-400 text-xs mt-0.5">Visitors must check in to attend this event</p>
                      </div>
                      <button onClick={() => updateEvent(index, "check_in_required", !ev.check_in_required)}
                        className={`relative w-[52px] h-[24px] rounded-full transition-colors duration-200 flex-shrink-0 ${ev.check_in_required ? "bg-blue-700" : "bg-gray-300"}`}>
                        <span className={`absolute top-[3px] w-[18px] h-[18px] bg-white rounded-full shadow transition-all duration-200 ${ev.check_in_required ? "left-[31px]" : "left-[3px]"}`} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
  
        <button onClick={addEvent} disabled={events.length >= 100}
          className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-gray-500 border-2 border-dashed border-gray-300 rounded-xl py-3 hover:border-blue-500 hover:text-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus className="w-4 h-4" /> Add Event
          <span className="text-xs text-gray-400 ml-1">({events.length}/100)</span>
        </button>
      </div>
    );
  };

  const validateEvent = (ev) => {
    const errs = {};
    if (!ev.event_name.trim()) errs.event_name = "required";
    if (!ev.host) errs.host = "required";
    if (!ev.location_id) errs.location_id = "required";
        const incomplete = ev.ranges.some((r) => !r.start_datetime || !r.end_datetime);
    if (incomplete) errs.ranges = "all ranges need start and end";
    const badOrder = ev.ranges.some(
      (r) => r.start_datetime && r.end_datetime &&
        new Date(r.end_datetime) <= new Date(r.start_datetime)
    );
    if (badOrder) errs.ranges = "end must be after start";
    return errs;
  };

// ─── Main Event Component ─────────────────────────────────────────────────────
const Event = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState("Single");
  const [hosts, setHosts] = useState([
    { name: "Raymond Chan" },
    { name: "Kae Yan" },
    { name: "Lenna Yeung" },
    { name: "Alan Yeung" },
  ]);
  
  const [locations, setLocations] = useState([
    { _id: "loc-001", location_name: "Digital Hub - Pass Office" },
    { _id: "loc-002", location_name: "Changi Creek - Pass Office" },
    { _id: "loc-003", location_name: "ST Engineering Hub - Innosuites" },
    { _id: "loc-004", location_name: "Gul Yard - Pass Office" },
  
  ]);

  const [singleForm, setSingleForm] = useState(blankEvent());
  const [singleErrors, setSingleErrors] = useState({});
  const [bulkEvents, setBulkEvents] = useState([blankEvent(), blankEvent()]);
  const [bulkErrors, setBulkErrors] = useState({});

  // Update clock
  useEffect(() => {
    const update = () => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch hosts & locations from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hostsRes, locationsRes] = await Promise.all([
          fetch("https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/events/hosts"),
          fetch("https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/events/locations"),
        ]);
        if (hostsRes.ok) setHosts(await hostsRes.json());
        if (locationsRes.ok) setLocations(await locationsRes.json());
      } catch (err) {
        console.error("Error fetching event data:", err);
      }
    };
    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    navigate("/");
  };



  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
        if (selectedType === "Single") {
          const errs = validateEvent(singleForm);
          setSingleErrors(errs);
          if (Object.keys(errs).length > 0) return;
          // TODO: POST /api/v1/external-vms/events
        } else {
          const allErrors = {};
          bulkEvents.forEach((ev, i) => {
            const errs = validateEvent(ev);
            if (Object.keys(errs).length > 0) allErrors[i] = errs;
          });
          setBulkErrors(allErrors);
          if (Object.keys(allErrors).length > 0) return;
          // TODO: POST /api/v1/external-vms/events/bulk
        }
      }
  };

  const handleBack = () => {
    if (currentStep === 2) setCurrentStep(1);
  };

  return (
    <div className="min-h-screen">
      <Sidebar onLogout={handleLogout} />

      {/* Time */}
      <div className="absolute top-4 right-6 text-gray-600 font-medium text-xl">
        {currentTime}
      </div>

      {/* Main content */}
      <div className="mx-auto min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-16">

          {currentStep === 1 && (
            <SelectEventType
              selectedType={selectedType}
              setSelectedType={setSelectedType}
            />
          )}

{currentStep === 2 && selectedType === "Single" && (
  <EventForm formData={singleForm} setFormData={setSingleForm}
    hosts={hosts} locations={locations} errors={singleErrors} isBulk={false} />
)}

{currentStep === 2 && selectedType === "Bulk" && (
  <BulkEventForm events={bulkEvents} setEvents={setBulkEvents}
    hosts={hosts} locations={locations} errors={bulkErrors} />
)}

        </div>

        {/* Footer */}
        <div className="px-8 pb-8">
          <div className="w-[92%] h-px ml-24 bg-gray-300 mb-6"></div>
          <div className="flex justify-between">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="px-8 py-3 ml-24 border border-gray-300 bg-white text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-8 py-3 bg-blue-700 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors ml-auto"
            >
{currentStep === 1 ? "Next" : selectedType === "Bulk" ? "Create Events" : "Create Event"}
</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Event;